import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useImportWinners, getRecognitionErrorMessage } from '@/api/recognition';

function downloadErrorsCsv(errorsCsv: string): void {
  const blob = new Blob([errorsCsv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'ganadores-errores.csv';
  anchor.click();
  URL.revokeObjectURL(url);
}

interface Props {
  file: File;
  modo: 'vigente' | 'historica';
  campaignId: string;
  badgeId: string;
  onBack: () => void;
  onNext: () => void;
}

/**
 * Paso 3 del asistente: PREVIEW automático al entrar (mismo criterio que
 * `bulkUpload/StepReviewRows` — `preview: true` corre toda la validación real contra el backend
 * sin escribir nada). A diferencia de la carga masiva de reconocimientos, aquí NO hay edición
 * inline fila por fila: el backend valida el ARCHIVO completo (`IWinnerWorkbookReader`), no filas
 * sueltas en JSON, así que corregir significa editar el Excel/CSV y volver al paso anterior a
 * elegir el archivo corregido — no se inventa un parseo/reconstrucción de Excel en el cliente.
 */
export function StepReview({ file, modo, campaignId, badgeId, onBack, onNext }: Readonly<Props>) {
  const { t } = useTranslation('recognition');
  const preview = useImportWinners();
  // Evita repetir el POST si el componente vuelve a renderizar con el MISMO archivo (p.ej. al
  // navegar de vuelta desde "Confirmar" sin haber cambiado nada) — solo se dispara con un
  // archivo/config nuevo.
  const clave = `${file.name}:${file.size}:${file.lastModified}:${modo}:${campaignId}:${badgeId}`;
  const claveEnviada = useRef<string | null>(null);

  useEffect(() => {
    // 🔴 Disparar `mutate()` en el mismo tick del efecto de montaje deja la mutación resuelta (el
    // fetch, el `onSuccess` y el `onSettled` corren bien) pero SIN re-render: la suscripción de
    // `useMutation` a este observador todavía no queda enganchada cuando el efecto de montaje
    // corre en el mismo commit (visto en vivo: la llamada automática se queda pegada en
    // "Validando archivo…" para siempre, pero una SEGUNDA llamada sobre el MISMO observador —p.
    // ej. disparada por un clic real— sí actualiza la pantalla). Diferir al siguiente macrotask
    // saca la llamada de ese commit inicial y la deja indistinguible de una interacción posterior.
    //
    // La marca de "ya enviada" se pone DENTRO del timeout, no antes de programarlo: en Strict Mode
    // (mount → cleanup → mount) el primer `setTimeout` se cancela en el cleanup antes de disparar,
    // y si `claveEnviada` ya quedara marcada desde el primer paso, el segundo (el que de verdad se
    // queda montado) vería la marca puesta y jamás programaría su propio timeout — ningún mutate()
    // llegaría a dispararse nunca.
    const id = setTimeout(() => {
      if (claveEnviada.current === clave) return;
      claveEnviada.current = clave;
      preview.mutate({
        file, modo,
        campaignId: modo === 'vigente' ? campaignId : undefined,
        // La insignia es obligatoria en LOS DOS modos (ajuste de UX): antes solo viajaba en
        // histórica y un ganador vigente nacía sin insignia.
        badgeId,
        preview: true,
      });
    }, 0);
    return () => clearTimeout(id);
    // Solo debe re-dispararse cuando la CLAVE cambia (archivo/config nuevos), no en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);

  const r = preview.data;

  return (
    <div className="bulk-step bulk-step--review">
      <p className="rp-note">{t('admin.winners.importWizard.reviewHint')}</p>

      {preview.isPending && (
        <p role="status" className="bulk-status-pending">
          <i className="fas fa-spinner fa-spin" /> {t('admin.winners.importWizard.previewPending')}
        </p>
      )}
      {preview.isError && (
        <p className="rp-error" role="alert">
          <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(preview.error, t('admin.winners.importError'))}
        </p>
      )}

      {r && (
        <>
          <div className="bulk-summary">
            <span className="bulk-summary-item"><strong>{r.totalFilas}</strong> {t('admin.winners.totalRows')}</span>
            <span className="bulk-summary-item bulk-summary-item--ok"><strong>{r.importados}</strong> {t('admin.winners.imported')}</span>
            <span className="bulk-summary-item bulk-summary-item--err"><strong>{r.rechazados}</strong> {t('admin.winners.rejected')}</span>
          </div>

          {r.errores.length === 0
            ? <p className="adm-success"><i className="fas fa-circle-check" aria-hidden /> {t('admin.winners.importWizard.noErrors')}</p>
            : (
              <>
                <div className="adm-table-wrap adm-table-wrap--spaced">
                  <table className="adm-table adm-table--glass">
                    <thead>
                      <tr>
                        <th>{t('admin.winners.row')}</th>
                        <th>{t('admin.winners.reason')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.errores.map((e, i) => (
                        <tr key={`${e.fila}-${e.codigo}-${i}`} className="adm-row--err">
                          <td>{e.fila === 0 ? t('admin.winners.importWizard.fileLevelError') : e.fila}</td>
                          <td>{e.motivo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {r.errorsCsv && (
                  <button type="button" className="btn btn--secondary" onClick={() => downloadErrorsCsv(r.errorsCsv)}>
                    <i className="fas fa-download" /> {t('bulk.downloadErrors')}
                  </button>
                )}
              </>
            )}
        </>
      )}

      <div className="bulk-wizard-nav">
        <button type="button" className="btn btn--secondary" onClick={onBack}>
          <i className="fas fa-arrow-left" /> {t('bulk.back')}
        </button>
        <button
          type="button"
          className="btn btn--send"
          disabled={!r || preview.isPending || r.importados === 0}
          onClick={onNext}
        >
          {t('bulk.next')} <i className="fas fa-arrow-right" />
        </button>
      </div>
    </div>
  );
}

export default StepReview;
